// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight, FiList, FiRefreshCcw } from "react-icons/fi";
import NoctisStatusDot from "../../components/noctis/NoctisStatusDot";
import { LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";
import { getNoctisOutboxStatus, listNoctisOutboxItems } from "../../api/noctisOutbox";

const formatNlDateTime = (value) => {
  if (value == null) return "—";
  const raw = typeof value === "string" ? value.trim() : value;
  if (raw === "") return "—";

  const parseToDate = (v) => {
    if (v == null) return null;
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
    if (typeof v === "number" && Number.isFinite(v)) {
      const ms = v < 1e12 ? v * 1000 : v;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const s = String(v).trim();
    if (!s) return null;
    if (/^\d+(?:\.\d+)?$/.test(s)) {
      const n = Number(s);
      if (!Number.isFinite(n)) return null;
      const ms = n < 1e12 ? n * 1000 : n;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const d = parseToDate(raw);
  if (!d) return String(value);
  try {
    return new Intl.DateTimeFormat("nl-NL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString();
  }
};

const normalizeByStatus = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((it) => ({
        status: String(it?.status || it?.key || it?.name || "").toUpperCase(),
        count: Number(it?.count ?? it?.value ?? 0),
      }))
      .filter((it) => it.status);
  }
  if (typeof value === "object") {
    return Object.entries(value).map(([k, v]) => ({
      status: String(k || "").toUpperCase(),
      count: Number(v ?? 0),
    }));
  }
  return [];
};

const summarizeError = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const firstLine = raw.split(/\r?\n/)[0] || raw;
  return firstLine
    .replace(/https?:\/\/[^\s"')]+/g, "…")
    .replace(/\s+/g, " ")
    .trim();
};

const extractOutboxItems = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload?.items,
    payload?.content,
    payload?.data,
    payload?.result,
    payload?.payload,
  ].filter((v) => v != null);

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (candidate && typeof candidate === "object") {
      const nested = candidate?.items ?? candidate?.content ?? candidate?.data;
      if (Array.isArray(nested)) return nested;
    }
  }

  return [];
};

const getSortableTimestamp = (value) => {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  if (typeof value === "number" && Number.isFinite(value)) return value < 1e12 ? value * 1000 : value;
  const s = String(value).trim();
  if (!s) return null;
  if (/^\d+(?:\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return n < 1e12 ? n * 1000 : n;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
};

export default function NoctisOutboxQueue() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inflightRef = useRef(false);
  const [filterStatus, setFilterStatus] = useState("PENDING");
  const [limit, setLimit] = useState(50);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerItem, setDrawerItem] = useState(null);

  const byStatus = useMemo(() => normalizeByStatus(status?.byStatus || status?.by_status), [status]);
  const totalAll = status?.total ?? status?.count ?? 0;
  const dueNow = status?.dueNow ?? status?.due_now ?? 0;
  const nextAttemptAt = status?.nextAttemptAt ?? status?.next_attempt_at ?? null;

  const byStatusSummary = useMemo(() => {
    const parts = (byStatus || [])
      .filter((it) => Number(it?.count) > 0 && it?.status)
      .sort((a, b) => String(a.status).localeCompare(String(b.status)))
      .map((it) => `${it.status}: ${Number(it.count) || 0}`);
    return parts.join(", ");
  }, [byStatus]);

  const totalFiltered = useMemo(() => {
    if (!filterStatus || filterStatus === "ALL") return Number(totalAll) || 0;
    const match = byStatus.find((it) => String(it?.status || "").toUpperCase() === String(filterStatus).toUpperCase());
    if (!match) return 0;
    return Number(match.count) || 0;
  }, [byStatus, filterStatus, totalAll]);

  const load = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const ALL_STATUSES = ["PENDING", "SENDING", "SENT", "DEAD"];
      const itemsPromise =
        filterStatus === "ALL"
          ? Promise.all(ALL_STATUSES.map((s) => listNoctisOutboxItems({ status: s, limit }))).then((payloads) => {
              const merged = payloads.flatMap((p) => extractOutboxItems(p));
              merged.sort((a, b) => {
                const aTs =
                  getSortableTimestamp(a?.createdAt ?? a?.created_at ?? a?.lastAttemptAt ?? a?.last_attempt_at) ?? -Infinity;
                const bTs =
                  getSortableTimestamp(b?.createdAt ?? b?.created_at ?? b?.lastAttemptAt ?? b?.last_attempt_at) ?? -Infinity;
                return bTs - aTs;
              });
              return merged.slice(0, Math.max(0, Number(limit) || 0));
            })
          : listNoctisOutboxItems({ status: filterStatus, limit });

      const [statusData, itemsData, reachabilityData] = await Promise.all([
        getNoctisOutboxStatus(),
        itemsPromise,
      ]);
      setStatus(statusData || null);
      setItems(extractOutboxItems(itemsData));
    } catch (e) {
      const statusCode = e?.response?.status;
      const url = e?.config?.baseURL && e?.config?.url ? `${e.config.baseURL}${e.config.url}` : e?.config?.url;
      const payload = e?.response?.data;
      const payloadText =
        typeof payload === "string"
          ? payload
          : payload != null
            ? JSON.stringify(payload)
            : "";
      const message =
        e?.response?.data?.message ||
        e?.message ||
        (statusCode ? `Request failed with status code ${statusCode}` : "Request failed");
      const details = [url ? `URL: ${url}` : null, statusCode ? `HTTP: ${statusCode}` : null, payloadText ? `Body: ${summarizeError(payloadText)}` : null]
        .filter(Boolean)
        .join(" • ");
      setError(details ? `${message} (${details})` : message);
    } finally {
      inflightRef.current = false;
      setLoading(false);
    }
  }, [filterStatus, limit]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FiList}
        eyebrow={t("noctis.menu", { defaultValue: "Noctis" })}
        title={t("noctis.outbox.title", { defaultValue: "Noctis queue" })}
        description={t("noctis.outbox.subtitle", { defaultValue: "Noctis outbox status" })}
        actions={
          <>
            <NoctisStatusDot />
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={loading}
            >
              <FiRefreshCcw />
              {loading ? t("common.loading", { defaultValue: "Loading..." }) : t("common.refresh", { defaultValue: "Refresh" })}
            </button>
          </>
        }
      />
      <div className="space-y-6 text-gray-900 dark:text-gray-100">
        <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          {error && (
            <div className="mt-4 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
              {error}
            </div>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <div className="text-xs font-semibold text-indigo-700 dark:text-indigo-200">
              {filterStatus && filterStatus !== "ALL" ? `Total (${filterStatus})` : "Total"}
            </div>
            <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{Number(totalFiltered) || 0}</div>
            {filterStatus === "ALL" && byStatusSummary && (
              <div className="mt-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                Breakdown: {byStatusSummary}
              </div>
            )}
            {filterStatus && filterStatus !== "ALL" && (
              <div className="mt-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                All statuses: {Number(totalAll) || 0}
              </div>
            )}
          </div>
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <div className="text-xs font-semibold text-indigo-700 dark:text-indigo-200">
              Due now
            </div>
            <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{Number(dueNow) || 0}</div>
          </div>
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:col-span-2">
            <div className="text-xs font-semibold text-indigo-700 dark:text-indigo-200">
              Next attempt at
            </div>
            <div className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">{formatNlDateTime(nextAttemptAt)}</div>
          </div>
        </div>

        <div className="rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <div className="px-6 pt-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
                {t("noctis.outbox.queue", { defaultValue: "Queue" })}
              </h3>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                <label className="inline-flex items-center gap-2">
                  {t("noctis.outbox.filter_status", { defaultValue: "Status" })}
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  >
                    {["ALL", "PENDING", "SENDING", "SENT", "DEAD"].map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="inline-flex items-center gap-2">
                  {t("noctis.outbox.limit", { defaultValue: "Limit" })}
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value || 0))}
                    className="w-24 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  />
                </label>
              </div>
            </div>
          </div>
          <div className="p-6 pt-4 space-y-6">
            <LdsTableCard>
              <LdsTableScroll>
              <LdsTable>
                <LdsTableHead>
                  <LdsTableRow>
                    <LdsTableHeadCell>Status</LdsTableHeadCell>
                    <LdsTableHeadCell>Event type</LdsTableHeadCell>
                    <LdsTableHeadCell>Attempts</LdsTableHeadCell>
                    <LdsTableHeadCell>Created at</LdsTableHeadCell>
                    <LdsTableHeadCell>Next attempt at</LdsTableHeadCell>
                    <LdsTableHeadCell>Last attempt at</LdsTableHeadCell>
                    <LdsTableHeadCell>Error</LdsTableHeadCell>
                  </LdsTableRow>
                </LdsTableHead>
                <LdsTableBody>
                  {items.length === 0 && (
                    <LdsTableRow>
                      <LdsTableCell className="text-gray-500 dark:text-gray-300" colSpan={7}>
                        {Number(totalAll) > 0 && Number(totalFiltered) === 0 && filterStatus !== "ALL"
                          ? t("noctis.outbox.items_empty_filtered", {
                              defaultValue: "No items for this status. There are items in other statuses.",
                            })
                          : t("noctis.outbox.items_empty", { defaultValue: "No items found." })}
                      </LdsTableCell>
                    </LdsTableRow>
                  )}
                  {items.map((it, idx) => {
                    const key = it?.id != null ? String(it.id) : `${idx}`;
                    const httpStatus = it?.lastHttpStatus ?? null;
                    const errText = it?.lastErrorMessage ? String(it.lastErrorMessage) : "";
                    const canOpen = Boolean(errText) || httpStatus != null;

                    const openDrawer = () => {
                      if (!canOpen) return;
                      setDrawerItem(it || null);
                      setDrawerOpen(true);
                    };

                    return (
                      <LdsTableRow
                        key={key}
                        className={canOpen ? "cursor-pointer" : ""}
                        onClick={openDrawer}
                        role={canOpen ? "button" : undefined}
                        tabIndex={canOpen ? 0 : undefined}
                        onKeyDown={(e) => {
                          if (!canOpen) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openDrawer();
                          }
                        }}
                      >
                        <LdsTableCell className="font-semibold text-gray-900 dark:text-gray-100">
                          {String(it?.status ?? "—")}
                        </LdsTableCell>
                        <LdsTableCell className="text-gray-900 dark:text-gray-100">
                          {String(it?.eventType ?? "—")}
                        </LdsTableCell>
                        <LdsTableCell className="font-mono text-gray-900 dark:text-gray-100">
                          {String(it?.attempts ?? 0)}
                        </LdsTableCell>
                        <LdsTableCell className="text-gray-900 dark:text-gray-100">
                          {formatNlDateTime(it?.createdAt)}
                        </LdsTableCell>
                        <LdsTableCell className="text-gray-900 dark:text-gray-100">
                          {formatNlDateTime(it?.nextAttemptAt)}
                        </LdsTableCell>
                        <LdsTableCell className="text-gray-900 dark:text-gray-100">
                          {formatNlDateTime(it?.lastAttemptAt)}
                        </LdsTableCell>
                        <LdsTableCell>
                          <div className="flex items-center justify-end gap-2">
                            {httpStatus != null && (
                              <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 font-mono text-xs font-semibold text-indigo-800 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-100">
                                HTTP {httpStatus}
                              </span>
                            )}
                            {canOpen ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 dark:text-indigo-200">
                                {t("common.details", { defaultValue: "Details" })}
                                <FiChevronRight />
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </div>
                        </LdsTableCell>
                      </LdsTableRow>
                    );
                  })}
                </LdsTableBody>
              </LdsTable>
              </LdsTableScroll>
            </LdsTableCard>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[9999]">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]"
            onClick={() => setDrawerOpen(false)}
            aria-label={t("common.close", { defaultValue: "Close" })}
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-xl overflow-y-auto border-l border-indigo-100/60 bg-white/95 p-6 shadow-2xl dark:border-indigo-500/30 dark:bg-slate-950/95">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Noctis outbox
                </div>
                <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {String(drawerItem?.eventType || drawerItem?.status || "Item")}
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {drawerItem?.status && (
                    <span className="inline-flex items-center rounded-full bg-indigo-600/15 px-3 py-1 text-xs font-semibold text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-100">
                      {String(drawerItem.status)}
                    </span>
                  )}
                  {(drawerItem?.lastHttpStatus ?? drawerItem?.last_http_status) != null && (
                    <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 font-mono text-xs font-semibold text-indigo-800 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-100">
                      HTTP {drawerItem?.lastHttpStatus ?? drawerItem?.last_http_status}
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                onClick={() => setDrawerOpen(false)}
              >
                {t("common.close", { defaultValue: "Close" })}
              </button>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-indigo-50/70 bg-indigo-50/40 px-3 py-2 text-sm dark:border-indigo-500/30 dark:bg-indigo-900/20">
                <p className="text-xs uppercase tracking-wide text-indigo-700 dark:text-indigo-200">Attempts</p>
                <p className="mt-1 font-semibold text-gray-900 dark:text-white">{drawerItem?.attempts ?? 0}</p>
              </div>
              <div className="rounded-2xl border border-indigo-50/70 bg-indigo-50/40 px-3 py-2 text-sm dark:border-indigo-500/30 dark:bg-indigo-900/20">
                <p className="text-xs uppercase tracking-wide text-indigo-700 dark:text-indigo-200">Created</p>
                <p className="mt-1 font-semibold text-gray-900 dark:text-white">{formatNlDateTime(drawerItem?.createdAt)}</p>
              </div>
              <div className="rounded-2xl border border-indigo-50/70 bg-indigo-50/40 px-3 py-2 text-sm dark:border-indigo-500/30 dark:bg-indigo-900/20">
                <p className="text-xs uppercase tracking-wide text-indigo-700 dark:text-indigo-200">Next attempt</p>
                <p className="mt-1 font-semibold text-gray-900 dark:text-white">{formatNlDateTime(drawerItem?.nextAttemptAt)}</p>
              </div>
              <div className="rounded-2xl border border-indigo-50/70 bg-indigo-50/40 px-3 py-2 text-sm dark:border-indigo-500/30 dark:bg-indigo-900/20">
                <p className="text-xs uppercase tracking-wide text-indigo-700 dark:text-indigo-200">Last attempt</p>
                <p className="mt-1 font-semibold text-gray-900 dark:text-white">{formatNlDateTime(drawerItem?.lastAttemptAt)}</p>
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Error
              </div>
              {(() => {
                const httpStatus = drawerItem?.lastHttpStatus ?? drawerItem?.last_http_status ?? null;
                const hasError = Boolean(drawerItem?.lastErrorMessage);
                if (!hasError && httpStatus == null) return null;
                return (
                  <div className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    HTTP status: {httpStatus == null ? "—" : httpStatus}
                  </div>
                );
              })()}
              <div className="mt-2 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 shadow-sm dark:border-rose-500/30 dark:bg-rose-900/20 dark:text-rose-100">
                <div className="whitespace-pre-wrap break-words">
                  {drawerItem?.lastErrorMessage ? String(drawerItem.lastErrorMessage) : "—"}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}

    </LdsOverviewPage>
  );
}
